/**
 * Oportunidades abiertas del mes. Cada fila abre **su registro exacto en
 * Close**, nunca el home ni una búsqueda.
 *
 * El link preferido es el de la oportunidad (`/lead/{leadId}/#opportunity-{id}`),
 * que es la página del lead ya anclada en la oportunidad. Cuando falta el id de
 * la oportunidad se cae al lead, y cuando falta el lead **no se renderiza ningún
 * `<a>`**: se muestra "sin vincular", igual que un KPI sin dato. Un link a una
 * 404 es peor que no tener link.
 *
 * La columna de plata (`valorEstimado`) solo existe para admin. Para comercial no
 * se dibuja: el servidor ya la manda en null, y un "—" en esa columna se leería
 * como "sin dato" cuando en realidad es "sin permiso".
 */
import type { Channel, FunnelItem, FunnelStage, Links, Role } from '@ops/contracts'

import { formatMetricValue } from '../../lib/format'
import { useVincular } from '../../lib/vincular'
import { type Column, DataTable, ExternalLink, Panel } from '../../ui'

export interface OportunidadesBlockProps {
  oportunidades: readonly FunnelItem[]
  etapas: readonly FunnelStage[]
  canales: readonly Channel[]
  /** Sobre de deep links del payload, indexado por id de oportunidad. */
  links: Links['oportunidades']
  rol: Role
}

export function OportunidadesBlock({
  oportunidades,
  etapas,
  canales,
  links,
  rol,
}: OportunidadesBlockProps) {
  const vincular = useVincular()

  const nombreEtapa = (stageId: string): string =>
    etapas.find((e) => e.id === stageId)?.nombre ?? stageId

  const nombreCanal = (canalId: string | null): string =>
    canalId === null
      ? 'Sin canal'
      : (canales.find((c) => c.id === canalId)?.nombre ?? canalId)

  const columnas: ReadonlyArray<Column<FunnelItem>> = [
    {
      key: 'nombre',
      header: 'Oportunidad',
      rowHeader: true,
      sortValue: (o) => o.nombre,
      render: (o) => o.nombre,
    },
    {
      key: 'etapa',
      header: 'Etapa',
      width: '120px',
      sortValue: (o) => nombreEtapa(o.stageId),
      render: (o) => nombreEtapa(o.stageId),
    },
    {
      key: 'canal',
      header: 'Canal',
      width: '160px',
      sortValue: (o) => nombreCanal(o.canalId),
      render: (o) => (
        <span className={o.canalId === null ? 'text-muted-strong' : undefined}>
          {nombreCanal(o.canalId)}
        </span>
      ),
    },
    {
      key: 'owner',
      header: 'Dueño',
      width: '140px',
      sortValue: (o) => o.owner?.nombre ?? null,
      render: (o) =>
        o.owner === null ? (
          <span className="text-micro text-muted-strong">Sin dueño</span>
        ) : (
          o.owner.nombre
        ),
    },
    {
      key: 'dias',
      header: 'Días en la etapa',
      align: 'right',
      width: '120px',
      sortValue: (o) => o.diasEnEtapa,
      render: (o) => formatMetricValue(o.diasEnEtapa, 'count'),
    },
    // La columna de plata no existe para quien no puede verla.
    ...(rol === 'admin'
      ? [
          {
            key: 'valor',
            header: 'Valor estimado',
            align: 'right' as const,
            width: '130px',
            sortValue: (o: FunnelItem) => o.valorEstimado,
            render: (o: FunnelItem) => formatMetricValue(o.valorEstimado, 'usd'),
          },
        ]
      : []),
    {
      key: 'close',
      header: 'En Close',
      width: '190px',
      render: (o) => {
        const sobre = links[o.id]
        const oportunidad = sobre?.oportunidad ?? null
        const lead = sobre?.close ?? null

        if (oportunidad !== null) {
          return (
            <ExternalLink link={oportunidad} variante="chip">
              Oportunidad en Close
            </ExternalLink>
          )
        }
        if (lead !== null) {
          return (
            <ExternalLink link={lead} variante="chip">
              Lead en Close
            </ExternalLink>
          )
        }
        // Sin lead no hay registro que abrir: estado "sin vincular" con la
        // acción, igual que un KPI sin dato lleva su "Cargalo acá".
        return (
          <ExternalLink
            link={null}
            sistemaFaltante="Close"
            onVincular={() => vincular(`«${o.nombre}» en Close`)}
          />
        )
      },
    },
  ]

  return (
    <Panel
      titulo="Oportunidades abiertas"
      subtitulo="Ordenadas por antigüedad en la etapa: arriba lo que lleva más tiempo quieto."
      padding="none"
    >
      <DataTable
        columns={columnas}
        rows={oportunidades}
        rowKey={(o) => o.id}
        density="compacta"
        filtroPlaceholder="oportunidad, canal o dueño"
        emptyMessage="Ninguna oportunidad cargada para este período"
        caption={
          <>
            Oportunidades del funnel. Cada fila abre su registro en Close, el
            sistema donde vive el lead.
          </>
        }
      />
    </Panel>
  )
}
